import { createSlice } from '@reduxjs/toolkit';
import { IMPERSONATION_ORIGIN_KEY } from '../../utils/impersonationStorage';
import { ROLES } from '../../config/roles';


// Note: KYC environment variable handling removed as it's not used in this slice

const kycFlag = process.env.REACT_APP_KYC_ENABLE_MODE === "TRUE"

// Helper function to safely get stored data
const getStoredAuth = () => {
  try {
    const tokens = localStorage.getItem("tokens");
    const user = localStorage.getItem("user");
    return {
      tokens: tokens ? JSON.parse(tokens) : null,
      user: user ? JSON.parse(user) : null,
    };
  } catch (error) {
    localStorage.removeItem("tokens");
    localStorage.removeItem("user");
    return { tokens: null, user: null };
  }
};

const storedAuth = getStoredAuth();

// resolveCurrentRole — THE single place that decides which profile perspective
// is active. Every write path (setCredentials / updateFromProfile / cold boot)
// goes through it, so the switcher can no longer be reset by a side effect.
//
//  1. A `preferred` role the user actually still holds always wins. That is the
//     choice `switchRole` persisted (onto state.user + localStorage) — it must
//     survive navigation, background token refreshes and reloads alike.
//  2. Otherwise the DEFAULT: a user who has BOTH an own agent profile and an
//     agency-admin seat starts on their OWN AGENT PROFILE (Marin 2026-08-11).
//     That pair is exactly the constellation the switcher is offered for
//     (components/layout/Header.js — `canSwitchRoles`), so this cannot disturb
//     anyone who has no switcher.
//  3. Everything else keeps the historical behaviour: first role wins. A pure
//     agency admin (no own agent profile) therefore stays on Agency, and club /
//     staff / lawyer / player constellations are untouched.
export const resolveCurrentRole = (roles, preferred = null) => {
  const roleList = Array.isArray(roles) ? roles : [];
  if (preferred && roleList.includes(preferred)) return preferred;
  if (roleList.includes(ROLES.Agent) && roleList.includes(ROLES.AgencyAdmin)) {
    return ROLES.Agent;
  }
  return roleList[0] || null;
};

const storedRoles = storedAuth.user?.groups || storedAuth.user?.roles || [];

const initialState = {
  user: storedAuth.user,
  tokens: storedAuth.tokens,
  loading: false,
  error: null,
  roles: storedRoles,
  // Read the persisted choice back — switchRole has always written it, but
  // until now nothing consumed it, so every reload fell back to roles[0].
  currentRole: resolveCurrentRole(storedRoles, storedAuth.user?.currentRole),
  permissions: storedAuth.user?.permissions || [],
  groups: storedAuth.user?.groups || [],
  kycVerified:  kycFlag ? storedAuth.user?.kycVerified || false : true,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      const { user, tokens } = action.payload;

      const incomingRoles = user?.groups || user?.roles || [];

      // setCredentials is not only "log in" — it is also re-issued for the SAME
      // user to stash a refreshed token (hooks/inbox/useInbox.js on every inbox
      // mount, services/api.js after a 401 refresh, components/layout/
      // PrivateLayout.js on rehydrate). Those must never silently reset the
      // profile switcher, so for the same identity the live choice carries over.
      // A genuine identity swap (login, "View as") resolves fresh instead, so a
      // superuser's perspective can never leak into the previewed account.
      const sameUser =
        state.user?.id != null &&
        user?.id != null &&
        String(state.user.id) === String(user.id);
      const preferredRole = user?.currentRole || (sameUser ? state.currentRole : null);
      const resolvedCurrentRole = resolveCurrentRole(incomingRoles, preferredRole);

      // Persist roles as groups for backward compatibility: store roles key using groups value
      const userToStore = {
        ...user,
        roles: incomingRoles,
        currentRole: resolvedCurrentRole,
      };

      state.user = userToStore;
      state.tokens = tokens;
      state.roles = incomingRoles;
      state.currentRole = resolvedCurrentRole;
      state.permissions = user?.permissions || [];
      state.groups = user?.groups || [];
      state.kycVerified = kycFlag ? user?.kycVerified || false : true;
      state.error = null;

      localStorage.setItem("tokens", JSON.stringify(tokens));
      localStorage.setItem("user", JSON.stringify(userToStore));
    },
    logout: (state) => {
      state.user = null;
      state.tokens = null;
      state.roles = [];
      // Drop the perspective too: a stale role left behind would otherwise be
      // offered as the "preferred" choice to whoever logs in next in this tab.
      state.currentRole = null;
      state.permissions = [];
      state.groups = [];
      state.kycVerified = kycFlag ? false : true;
      state.error = null;
      localStorage.removeItem("tokens");
      localStorage.removeItem("user");
      // Logging out mid "View as" preview must not leave the stashed
      // superuser session behind: a stale impersonation_origin would make the
      // NEXT login boot with a false preview banner and an exit path into the
      // stale credentials. Covers all logout dispatch sites (UI + the
      // refresh-failure paths in api.js).
      localStorage.removeItem(IMPERSONATION_ORIGIN_KEY);
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    switchRole: (state, action) => {
      state.currentRole = action.payload;

      // Mirror the choice onto the in-memory user as well. Consumers that
      // re-issue credentials spread the CURRENT user object (useInbox.js builds
      // `{...store.getState().auth.user, streamChatId}`), so without this the
      // stale login-time role would ride along and undo the switch.
      if (state.user) {
        state.user.currentRole = action.payload;
      }

      // Persist chosen role into stored user for durability across refreshes and future credential updates
      try {
        const storedUserRaw = localStorage.getItem("user");
        if (storedUserRaw) {
          const storedUser = JSON.parse(storedUserRaw);
          const updatedStoredUser = {
            ...storedUser,
            currentRole: action.payload,
          };
          localStorage.setItem("user", JSON.stringify(updatedStoredUser));
        }
      } catch (e) {
        // No-op if localStorage is unavailable or invalid JSON
      }
    },
    updateUserTypeSelected: (state) => {
      if (state.user) {
        state.user.userTypeSelected = true;
        localStorage.setItem("user", JSON.stringify(state.user));
        console.log("User type selected updated to true");
      }
    },
    // Merge profile-derived permissions/roles/groups/kycVerified without clobbering currentRole unnecessarily
    updateFromProfile: (state, action) => {
      const incomingPermissions = Array.isArray(action.payload?.permissions)
        ? action.payload.permissions
        : state.permissions || [];

      const payloadGroups = Array.isArray(action.payload?.groups)
        ? action.payload.groups
        : undefined;
      const payloadRoles = Array.isArray(action.payload?.roles)
        ? action.payload.roles
        : undefined;
      const incomingKycVerified = typeof action.payload?.kycVerified === 'boolean'
        ? action.payload.kycVerified
        : state.kycVerified || false;

      // Prefer groups when available; fallback to roles; finally existing roles
      const incomingRoles =
        payloadGroups && payloadGroups.length > 0
          ? payloadGroups
          : payloadRoles || state.roles || [];

      // Preserve currentRole if still valid; otherwise fall back to the default
      // resolution (own agent profile before the agency seat).
      const resolvedCurrentRole = resolveCurrentRole(
        incomingRoles,
        state.currentRole
      );

      // Display names (T012): merge only when the profile sync actually
      // provides them, so a payload without names never wipes stored ones.
      const nameString = (v) => (typeof v === 'string' && v.trim() ? v : undefined);
      const incomingName = nameString(action.payload?.name);
      const incomingClubName = nameString(action.payload?.club_name);
      const incomingAgencyName = nameString(action.payload?.agency_name);

      // Update state.user while keeping other fields intact
      if (state.user) {
        const updatedUser = {
          ...state.user,
          permissions: incomingPermissions,
          groups: Array.isArray(payloadGroups)
            ? payloadGroups
            : state.user.groups || [],
          roles: incomingRoles,
          currentRole: resolvedCurrentRole,
          kycVerified: incomingKycVerified,
          ...(incomingName ? { name: incomingName } : {}),
          ...(incomingClubName ? { club_name: incomingClubName } : {}),
          ...(incomingAgencyName ? { agency_name: incomingAgencyName } : {}),
        };
        state.user = updatedUser;
        try {
          localStorage.setItem("user", JSON.stringify(updatedUser));
        } catch (_) {
          // ignore storage errors
        }
      }

      state.permissions = incomingPermissions;
      state.groups = Array.isArray(payloadGroups)
        ? payloadGroups
        : state.groups || [];
      state.roles = incomingRoles;
      state.currentRole = resolvedCurrentRole;
      state.kycVerified = incomingKycVerified;
      state.error = null;
    },
  },
});

export const {
  setCredentials,
  logout,
  setError,
  switchRole,
  updateUserTypeSelected,
  updateFromProfile,
} = authSlice.actions;

export default authSlice.reducer;

export const selectUserRoles = (state) => state.auth.roles;
export const selectCurrentRole = (state) => state.auth.currentRole;
export const selectUserPermissions = (state) => state.auth.permissions;
export const selectUserGroups = (state) => state.auth.groups;
